// Constants
import { AnalyticsConstants, CustomerTypes, LoanProductTypes, EmploymentLength, IdTypeSelections, ReturnCustIndicatorTypes } from '../../constants'

// Utilities
import isEmpty from '../../utility/isEmpty.js'

export default {
  maritalStatusRequired (customerState) {
    return customerState.state === 'WI'
  },

  customerType (customerState) {
    if (isEmpty(customerState.applicationId)) {
      return undefined // Do not return customerType until we have an application
    }

    return customerState.newCustomer ? CustomerTypes.new : CustomerTypes.returning
  },

  /**
   * The customer type for analytics does not effect the flow in the application like normal customerType so a separate property is needed.
   */
  customerTypeAnalytics (customerState, getters) {
    if (isEmpty(customerState.applicationId)) {
      return undefined // Do not return customerType until we have an application
    }

    if (!isEmpty(customerState.newCustomer)) {
      return getters.customerType
    }

    return customerState.newCustomerForAnalytics ? CustomerTypes.new : CustomerTypes.returning
  },

  /**
   * Gets the customers full name (first, middle, last, suffix).
   * @returns {String} The customer's full name.
   */
  fullName (customerState) {
    return [customerState.firstName, customerState.middleName, customerState.lastName, customerState.suffixName].filter(name => !isEmpty(name)).join(' ')
  },

  loanProduct (customerState) {
    return customerState.loanProduct || LoanProductTypes.titleLoan
  },

  isTitleLoanProduct (customerState, getters) {
    return getters.loanProduct === LoanProductTypes.titleLoan
  },

  isSignatureLoanProduct (customerState, getters) {
    return getters.loanProduct === LoanProductTypes.signatureLoan
  },

  analyticProductType (customerState) {
    return customerState.loanProduct === LoanProductTypes.signatureLoan ? AnalyticsConstants.attributeValues.productType.signature : AnalyticsConstants.attributeValues.productType.title
  },

  analyticLoanProductType (customerState) {
    return customerState.loanProduct === LoanProductTypes.signatureLoan ? AnalyticsConstants.attributeValues.loanProductType.signature : AnalyticsConstants.attributeValues.loanProductType.title
  },

  employmentHoursMet (customerState, getters, rootState) {
    return !rootState.applicationSession.config.showEmploymentLengthQuestions ? undefined : isEmpty(customerState.employmentPerWeek) ? rootState.applicationIncome.incomeList.length > 0 : customerState.employmentPerWeek === EmploymentLength.hours_yes
  },

  employmentLengthMet (customerState, getters, rootState) {
    return !rootState.applicationSession.config.showEmploymentLengthQuestions ? undefined : isEmpty(customerState.employmentLength) ? rootState.applicationIncome.incomeList.length > 0 : customerState.employmentLength === EmploymentLength.length_yes
  },

  isVehicleOwned (customerState, getters, rootState) {
    return customerState.hasTitle || !rootState.applicationSession.showOwnershipScreen
  },

  isFloridaState (customerState) {
    return customerState.state === 'FL'
  },

  isNewMexicoState (customerState) {
    return customerState.state === 'NM'
  },

  isUtahState (customerState) {
    return customerState.state === 'UT'
  },

  isNevadaState (customerState) {
    return customerState.state === 'NV'
  },

  isSouthCarolinaState (customerState) {
    return customerState.state === 'SC'
  },

  returnUser (customerState, getters, rootState) {
    if (rootState.session.accountCreatedSession) { // Disregard customer indicator if account created during this session
      return false
    }

    return customerState.returnCustIndicator === ReturnCustIndicatorTypes.personal || customerState.returnCustIndicator === ReturnCustIndicatorTypes.financial || !customerState.newCustomer
  },

  /**
   * Returns the name of the page after submission where a user can set an appointment or download FTA based on if FTA is offered. Optionally FTA offered flag can be passed in.
   * @returns {String} The final page after submission to navigate users to.
   */
  postSubmissionPage: (customerState, getters, rootState, rootGetters) => (appraisalOffered = undefined) => {
    if (appraisalOffered === undefined) {
      const allowSelfAppraisal = customerState.allowSelfAppraisal
      appraisalOffered = !isEmpty(allowSelfAppraisal) && allowSelfAppraisal
    }

    if (rootGetters['applicationSession/disableSelfAppraisal'] || !appraisalOffered) { // If appraisal is not offered or turned off, send to old page to show appointment only options
      return 'AppointmentOnly'
    }

    return 'DownloadAssessmentFastTrackWeb'
  },

  /**
   * Gets whether the currently selected ID type can be checked against user geolocation or not (DL and state ID can, passport and matricula cannot).
   * @returns {Boolean} True if the currently selected ID type can be checked against user geolocation.
   */
  isGeolocatedIdType (customerState) {
    return customerState.idSelection === IdTypeSelections.driversLicense || customerState.idSelection === IdTypeSelections.state
  }
}
